import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

// ─── Toast ───────────────────────────────────────────────────────────────────
const Toast = ({ toasts }) => (
  <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
    {toasts?.map((t) => (
      <div
        key={t?.id}
        className="px-4 py-3 rounded-lg text-sm font-medium shadow-lg pointer-events-auto"
        style={{
          backgroundColor: t?.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${t?.type === 'success' ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
          color: t?.type === 'success' ? '#22c55e' : '#ef4444',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {t?.message}
      </div>
    ))}
  </div>
);

// ─── Server Badge ─────────────────────────────────────────────────────────────
const ServerBadge = ({ server, serverColors }) => {
  const color = serverColors?.[server] || '#6b7280';
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-bold"
      style={{
        backgroundColor: `${color}18`,
        color,
        border: `1px solid ${color}30`,
        fontFamily: 'Sora, sans-serif',
      }}
    >
      {server}
    </span>
  );
};

// ─── Stats Header ─────────────────────────────────────────────────────────────
const StatsHeader = ({ assignments, managers }) => {
  const { t } = useTranslation();
  const total = assignments?.length || 0;
  const unassigned = assignments?.filter((a) => !a?.manager_id)?.length || 0;
  const assigned = assignments?.filter((a) => a?.manager_id)?.length || 0;
  const activeManagerIds = new Set(
    assignments?.filter((a) => a?.manager_id)?.map((a) => a?.manager_id)
  );
  const activeManagers = activeManagerIds?.size || 0;

  const cards = [
    { label: t('admin.assignments.totalGroups', 'Total groupes'), value: total, color: '#3b82f6' },
    { label: t('admin.assignments.unassigned', 'Non assignés'), value: unassigned, color: '#f97316', alert: unassigned > 0 },
    { label: t('admin.assignments.assigned', 'Assignés'), value: assigned, color: '#22c55e' },
    { label: t('admin.assignments.activeManagers', 'Managers actifs'), value: activeManagers, color: '#a855f7' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {cards?.map((card) => (
        <div
          key={card?.label}
          className="rounded-xl border p-4"
          style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
        >
          <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
            {card?.label}
          </p>
          <div className="flex items-center gap-2">
            <span
              className="text-2xl font-bold"
              style={{ color: card?.color, fontFamily: 'Sora, sans-serif' }}
            >
              {card?.value}
            </span>
            {card?.alert && card?.value > 0 && (
              <span
                className="px-1.5 py-0.5 rounded text-xs font-bold"
                style={{ backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)', fontFamily: 'Sora, sans-serif' }}
              >
                !
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Unassigned Card ──────────────────────────────────────────────────────────
const UnassignedCard = ({ assignment, managers, serverColors, onSave }) => {
  const { t } = useTranslation();
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedManagerId) return;
    setSaving(true);
    await onSave(assignment?.id, selectedManagerId, assignment?.group_name, assignment?.server);
    setSaving(false);
  };

  const color = serverColors?.[assignment?.server] || '#6b7280';

  return (
    <div
      className="rounded-xl border p-4 flex flex-col gap-3"
      style={{
        backgroundColor: '#111111',
        borderColor: '#1f1f1f',
        borderLeft: '3px solid #f97316',
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Sora, sans-serif' }}>
            {assignment?.group_name}
          </span>
          <ServerBadge server={assignment?.server} serverColors={serverColors} />
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded flex-shrink-0"
          style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)', fontFamily: 'Inter, sans-serif' }}
        >
          {t('admin.assignments.unassigned', 'Non assigné')}
        </span>
      </div>
      <div className="flex gap-2">
        <select
          value={selectedManagerId}
          onChange={(e) => setSelectedManagerId(e?.target?.value)}
          className="flex-1 px-3 py-2 rounded-lg text-base text-white outline-none border"
          style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
        >
          <option value="">{t('admin.assignments.selectManager', '— Sélectionner un manager —')}</option>
          {managers?.map((m) => (
            <option key={m?.id} value={m?.id}>
              {m?.name}
            </option>
          ))}
        </select>
        <button
          onClick={handleSave}
          disabled={!selectedManagerId || saving}
          className="px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-40 flex-shrink-0"
          style={{ backgroundColor: '#f97316', fontFamily: 'Sora, sans-serif' }}
        >
          {saving ? t('admin.assignments.saving', 'Enregistrement...') : t('admin.assignments.save', 'Sauvegarder')}
        </button>
      </div>
    </div>
  );
};

// ─── Assigned Table Row ───────────────────────────────────────────────────────
const AssignedRow = ({ assignment, managers, serverColors, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState(assignment?.manager_id || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(assignment?.id, selectedManagerId, assignment?.group_name, assignment?.server);
    setSaving(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setSelectedManagerId(assignment?.manager_id || '');
    setEditing(false);
  };

  const managerName = assignment?.users?.name || '—';
  const updatedAt = assignment?.updated_at
    ? new Date(assignment?.updated_at)?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <tr
      className="border-t transition-colors"
      style={{ borderColor: '#1f1f1f' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1a1a1a'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <td className="px-4 py-3 font-medium text-white text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
        {assignment?.group_name}
      </td>
      <td className="px-4 py-3">
        <ServerBadge server={assignment?.server} serverColors={serverColors} />
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <select
            value={selectedManagerId}
            onChange={(e) => setSelectedManagerId(e?.target?.value)}
            className="px-2 py-1.5 rounded-lg text-base text-white outline-none border"
            style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif', minWidth: '160px' }}
          >
            <option value="">{t('admin.assignments.none', '— Aucun —')}</option>
            {managers?.map((m) => (
              <option key={m?.id} value={m?.id}>
                {m?.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: 'Inter, sans-serif' }}>
            {managerName}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
        {updatedAt}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-colors disabled:opacity-40"
              style={{ backgroundColor: '#22c55e', fontFamily: 'Sora, sans-serif' }}
            >
              {saving ? t('admin.assignments.saving', 'Enregistrement...') : t('admin.assignments.save', 'Sauvegarder')}
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors"
              style={{ borderColor: '#2a2a2a', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent', fontFamily: 'Inter, sans-serif' }}
            >
              {t('admin.assignments.cancel', 'Annuler')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
            style={{ backgroundColor: 'rgba(59,130,246,0.12)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.25)', fontFamily: 'Sora, sans-serif' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.12)'; }}
          >
            {t('admin.assignments.edit', 'Modifier')}
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ManagerAssignmentGroups = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [servers, setServers] = useState([]);
  const [serverColors, setServerColors] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [serverFilter, setServerFilter] = useState('Tous');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [searchTerm, setSearchTerm] = useState('');

  // Toast
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev?.filter((t) => t?.id !== id)), 3500);
  }, []);

  // ── Fetch servers ─────────────────────────────────────────────────────────
  const fetchServers = useCallback(async () => {
    const { data } = await supabase?.from('servers')?.select('id, name, color')?.order('id');
    const list = data || [];
    setServers(list);
    const colorMap = {};
    list?.forEach((s) => { colorMap[s?.id] = s?.color || '#6b7280'; });
    setServerColors(colorMap);
  }, []);

  // ── Fetch managers ────────────────────────────────────────────────────────
  const fetchManagers = useCallback(async () => {
    const { data } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'region_manager'])?.order('name');
    setManagers(data || []);
  }, []);

  // ── Fetch assignments + auto-detect new groups from creators ──────────────
  const fetchAssignments = useCallback(async () => {
    // 1. Get existing assignments
    const { data: existingAssignments } = await supabase
      ?.from('manager_assignments')
      ?.select(`
        id,
        group_name,
        server,
        manager_id,
        assigned_at,
        updated_at,
        users!manager_assignments_manager_id_fkey(id, name)
      `)
      ?.order('server')
      ?.order('group_name');

    // 2. Get all distinct group_name + server from creators
    const { data: creatorGroups } = await supabase
      ?.from('creators')
      ?.select('group_name, server')
      ?.not('group_name', 'is', null);

    const existingKeys = new Set(
      (existingAssignments || []).map(a => `${a.group_name}__${a.server}`)
    );

    // 3. Find groups in creators that don't exist in manager_assignments
    const newGroupsMap = {};
    (creatorGroups || []).forEach(c => {
      if (!c.group_name || !c.server) return;
      const key = `${c.group_name}__${c.server}`;
      if (!existingKeys.has(key) && !newGroupsMap[key]) {
        newGroupsMap[key] = { group_name: c.group_name, server: c.server };
      }
    });

    const newGroups = Object.values(newGroupsMap);

    // 4. Auto-insert new groups into manager_assignments (unassigned)
    if (newGroups.length > 0) {
      await supabase?.from('manager_assignments')?.insert(
        newGroups.map(g => ({ group_name: g.group_name, server: g.server, manager_id: null }))
      );
      // Re-fetch after insert
      const { data: refreshed } = await supabase
        ?.from('manager_assignments')
        ?.select(`id, group_name, server, manager_id, assigned_at, updated_at, users!manager_assignments_manager_id_fkey(id, name)`)
        ?.order('server')
        ?.order('group_name');
      setAssignments(refreshed || []);
    } else {
      setAssignments(existingAssignments || []);
    }
  }, []);

  // ── Initial load ──────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchServers(), fetchManagers(), fetchAssignments()]);
    setLoading(false);
  }, [fetchServers, fetchManagers, fetchAssignments]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Save assignment ───────────────────────────────────────────────────────
  const handleSave = useCallback(async (assignmentId, selectedManagerId, groupName, server) => {
    const { data, error } = await supabase?.rpc('assign_manager_to_group', {
      p_group_name: groupName,
      p_server: server,
      p_manager_id: selectedManagerId || null,
    });

    if (error || !data?.success) {
      addToast(t('admin.assignments.errorAssign', 'Erreur') + ': ' + (data?.error || error?.message), 'error');
    } else {
      addToast(
        t('admin.assignments.assignSuccess', 'Groupe "{{group}}" assigné — {{count}} créateurs mis à jour', { group: groupName, count: data?.updated_creators }),
        'success'
      );
      await fetchAssignments();
    }
  }, [addToast, fetchAssignments]);

  // ── Filter logic ──────────────────────────────────────────────────────────
  const filtered = assignments?.filter((a) => {
    if (serverFilter !== 'Tous' && a?.server !== serverFilter) return false;
    if (statusFilter === 'Non assignés' && a?.manager_id) return false;
    if (statusFilter === 'Assignés' && !a?.manager_id) return false;
    if (searchTerm && !a?.group_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())) return false;
    return true;
  });

  const unassigned = filtered?.filter((a) => !a?.manager_id);
  const assigned = filtered?.filter((a) => a?.manager_id);

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} userEmail="" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.assignments.title', 'Groupes & Assignments')} />
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b" style={{ borderColor: '#1f1f1f', backgroundColor: 'var(--color-background)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.assignments.title', 'Groupes & Assignments')}
              </h1>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                {t('admin.assignments.subtitle', 'Gestion des assignations manager par groupe')}
              </p>
            </div>
            <button
              onClick={loadAll}
              className="px-3 py-2 rounded-lg text-xs font-medium border transition-colors"
              style={{ borderColor: '#2a2a2a', color: 'rgba(255,255,255,0.5)', backgroundColor: 'transparent', fontFamily: 'Inter, sans-serif' }}
            >
              ↻ {t('admin.assignments.refresh', 'Actualiser')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-sm" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                {t('admin.assignments.loading', 'Chargement...')}
              </div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <StatsHeader assignments={assignments} managers={managers} />

              {/* Filter Bar */}
              <div className="rounded-xl border p-4 mb-6 flex flex-wrap gap-3 items-center" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                {/* Search */}
                <div className="relative flex-1 min-w-48">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" />
                    <path d="M9.5 9.5L12 12" stroke="rgba(255,255,255,0.3)" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t('admin.assignments.searchPlaceholder', 'Rechercher un groupe...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-8 pr-3 py-2 rounded-lg text-base text-white outline-none border"
                    style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                {/* Server tabs */}
                <div className="flex gap-1 flex-wrap">
                  {['Tous', ...servers?.map((s) => s?.id)]?.map((srv) => {
                    const isActive = serverFilter === srv;
                    const color = srv !== 'Tous' ? (serverColors?.[srv] || '#6b7280') : '#E5202A';
                    return (
                      <button
                        key={srv}
                        onClick={() => setServerFilter(srv)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                        style={{
                          backgroundColor: isActive ? `${color}20` : 'transparent',
                          color: isActive ? color : 'rgba(255,255,255,0.4)',
                          border: `1px solid ${isActive ? `${color}40` : '#2a2a2a'}`,
                          fontFamily: 'Sora, sans-serif',
                        }}
                      >
                        {srv === 'Tous' ? t('admin.assignments.allServers', 'All') : srv}
                      </button>
                    );
                  })}
                </div>

                {/* Status filter */}
                <div className="flex gap-1">
                  {[
                  { key: 'Tous', label: t('admin.assignments.filterAll', 'Tous') },
                  { key: 'Non assignés', label: t('admin.assignments.filterUnassigned', 'Non assignés') },
                  { key: 'Assignés', label: t('admin.assignments.filterAssigned', 'Assignés') },
                ]?.map(({ key: s, label: statusLabel }) => {
                    const isActive = statusFilter === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          backgroundColor: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                          color: isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                          border: `1px solid ${isActive ? '#3a3a3a' : '#2a2a2a'}`,
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {statusLabel}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* SECTION 1: Non assignés */}
              {(statusFilter === 'Tous' || statusFilter === 'Non assignés') && unassigned?.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-sm font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
                      {t('admin.assignments.unassigned', 'Non assignés')}
                    </h2>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-bold"
                      style={{ backgroundColor: 'rgba(249,115,22,0.15)', color: '#f97316', border: '1px solid rgba(249,115,22,0.3)', fontFamily: 'Sora, sans-serif' }}
                    >
                      {unassigned?.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {unassigned?.map((a) => (
                      <UnassignedCard
                        key={a?.id}
                        assignment={a}
                        managers={managers}
                        serverColors={serverColors}
                        onSave={handleSave}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 2: Assignés */}
              {(statusFilter === 'Tous' || statusFilter === 'Assignés') && assigned?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-sm font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                      {t('admin.assignments.assigned', 'Assignés')}
                    </h2>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-bold"
                      style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)', fontFamily: 'Sora, sans-serif' }}
                    >
                      {assigned?.length}
                    </span>
                  </div>
                  <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                            {[
                              t('admin.assignments.colGroup', 'Groupe'),
                              t('admin.assignments.colServer', 'Serveur'),
                              t('admin.assignments.colManager', 'Manager'),
                              t('admin.assignments.colLastModified', 'Dernière modification'),
                              t('admin.assignments.colActions', 'Actions'),
                            ]?.map((col) => (
                              <th
                                key={col}
                                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                                style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', backgroundColor: '#111111' }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {assigned?.map((a) => (
                            <AssignedRow
                              key={a?.id}
                              assignment={a}
                              managers={managers}
                              serverColors={serverColors}
                              onSave={handleSave}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {filtered?.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center h-48 rounded-xl border" style={{ borderColor: '#1f1f1f', backgroundColor: '#111111' }}>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                    {t('admin.assignments.noGroupFound', 'Aucun groupe trouvé')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Toast toasts={toasts} />
      <AdminBottomNav />
    </div>
  );
};

export default ManagerAssignmentGroups;
